import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  LOCAL_STORAGE_CONSTANT,
  PAYMENT_INTERVAL,
  SLUGS_TO_FILTERS,
} from '../constants/app';
import { APP_ROUTES, COURSES_SLUGS, getRoute } from '../route';
import { extractApiError, joinPlanWaitingListRequest } from '../api/community';
import {
  createNotification,
  NOTIFICATION_TYPE,
} from '../services/Notification';
import { doGetPublicPlansRequest } from '../store/action/challengesAndCourses';
import {
  setSelectedFilters,
  setUniversalLoading,
} from '../store/action/common';
import { doPaymentInitiationRequest } from '../store/action/products';
import { getUserAttribute } from '../utils/helpers';
import { encodeBase64Value, encodeURLParams } from '../utils/url';
import { getItem } from '../utils/cache';
import { extractJSON } from '../services/jsonUtil';

// Callers used to hand this hook the v5 `history` and `match` route props.
// Under v6 it sources both itself; `matchOverride` remains for the cached
// `localMatch` value some flows stash in storage, and the URL wins when it
// actually carries a slug.
export const useCourseDetail = (
  matchOverride,
  p_intervalArg,
  skipInitialFetch = false,
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const match = params?.slug ? { params } : matchOverride;
  const p_interval =
    p_intervalArg ||
    (['lowback', 'LowBackFB'].includes(params.slug)
      ? PAYMENT_INTERVAL.YEARLY
      : PAYMENT_INTERVAL.MONTHLY);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const localSlug = extractJSON('localSlug');
  const stableLocalSlug =
    typeof localSlug === 'string' ? localSlug : JSON.stringify(localSlug) ?? '';

  const getSlugFromMatch = value => {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value)?.params?.slug;
      } catch (error) {
        return null;
      }
    }
    return value?.params?.slug;
  };

  const normalizeSlug = value =>
    typeof value === 'string'
      ? value.toLowerCase().trim().replace(/-/g, ' ').replace(/\s+/g, ' ')
      : '';

  const getStoredCourseSlug = () => {
    const fromMatch = getSlugFromMatch(extractJSON('localMatch'));
    if (fromMatch) return fromMatch;
    const storedSlug = getItem('localSlug');
    return typeof storedSlug === 'string' && storedSlug[0] !== '{'
      ? storedSlug
      : getSlugFromMatch(storedSlug);
  };

  const getCurrentSlug = () => {
    const urlParams = new URLSearchParams(location.search);
    return (
      getSlugFromMatch(match) || urlParams.get('page') || getStoredCourseSlug()
    );
  };

  // const [isCourseLoading, setIsCourseLoading] = useState(false);
  const [courseDetail, setCourseDetail] = useState(() => {
    try {
      const savedCourse = sessionStorage.getItem('courseDetail');
      if (!savedCourse) return null;

      const parsedCourse = JSON.parse(savedCourse);
      const requestedSlug = normalizeSlug(getCurrentSlug());
      const savedSlug = normalizeSlug(parsedCourse?.attributes?.slug);

      // Clear stale session data when route slug and cached slug do not match.
      if (requestedSlug && savedSlug && requestedSlug !== savedSlug) {
        sessionStorage.removeItem('courseDetail');
        return null;
      }

      return parsedCourse;
    } catch (error) {
      console.error('Error reading from sessionStorage', error);
      return null;
    }
  });
  const [challengeError, setChallengeError] = useState(false);
  const { selectedPlan } = useSelector(state => state.product);
  // const { publicPlans } = useSelector(state => state.challengesAndCourses);
  // const redirectToCourse = getItem('redirectToCourse');

  useEffect(() => {
    // This effect syncs the courseDetail state with sessionStorage.
    try {
      if (courseDetail) {
        sessionStorage.setItem('courseDetail', JSON.stringify(courseDetail));
      } else {
        // If courseDetail is null, remove it from storage.
        sessionStorage.removeItem('courseDetail');
      }
    } catch (error) {
      console.error('Error writing to sessionStorage', error);
    }
  }, [courseDetail]);

  useEffect(() => {
    if (skipInitialFetch) {
      return;
    }
    const slug = getCurrentSlug();
    const interval = selectedPlan
      ? selectedPlan?.attributes?.interval
      : p_interval;
    if (slug) {
      dispatch(
        doGetPublicPlansRequest({
          interval,
          callback: plans => {
            if (typeof slug === 'string') {
              const course = plans.find(
                plan =>
                  normalizeSlug(plan?.attributes?.slug) === normalizeSlug(slug),
              );
              setCourseDetail(course || null);
            }
            setIsLoading(false);
          },
        }),
      );
    } else {
      setCourseDetail(null);
      setIsLoading(false);
    }
  }, [
    location.search,
    stableLocalSlug,
    params.slug,
    p_interval,
    selectedPlan?.attributes?.interval,
    skipInitialFetch,
  ]);

  const getRedirectionURL = courseDetail => {
    const { card_color, title, plan_type } = courseDetail?.attributes || {};
    const plan_id = courseDetail?.id;
    const params = {
      card_color,
      plan_type,
      title,
    };
    const options = {
      pathParam: {
        id: plan_id,
      },
      searchParam: {
        params: encodeURLParams(params),
      },
    };
    const url =
      plan_type === 'plan'
        ? APP_ROUTES.HOME_SUB_SECTION
        : APP_ROUTES.PREMIUM_COURSES_DETAILS;
    const route = getRoute(url, options);
    return route;
  };

  const getPurchaseKey = plan_type => {
    switch (plan_type) {
      case 'challenge':
        return 'challenges';
      case 'course':
        return 'courses';
      default:
        return plan_type;
    }
  };

  const getOrderErrors = error => {
    const errors =
      error?.response?.data?.response?.errors ||
      error?.response?.data?.errors ||
      error?.response?.data?.error;

    if (Array.isArray(errors)) return errors.flat();
    return errors ? [errors] : [];
  };

  const isActivePurchaseError = error =>
    getOrderErrors(error).some(message =>
      String(message).toLowerCase().includes('active purchase'),
    );

  const isCourseAlreadyPurchased = course => {
    const { plan_type } = course?.attributes || {};
    const purchases = getUserAttribute('purchases');
    const subscriptionDetails = getUserAttribute('subscription_details');
    const { ends_at, subscribed } = subscriptionDetails || {};
    const activeSubscription =
      subscribed && ends_at && new Date() <= new Date(ends_at);
    return plan_type === 'plan'
      ? activeSubscription
      : purchases?.[getPurchaseKey(plan_type)]?.includes(Number(course?.id));
  };

  const redirectUser = courseDetail => {
    const { title, product_id, plan_type } = courseDetail?.attributes || {};
    const plan_id = courseDetail?.id;
    const route = getRedirectionURL(courseDetail);
    const purchases = getUserAttribute('purchases');
    const subscriptionDetails = getUserAttribute('subscription_details');
    const { ends_at, subscribed } = subscriptionDetails || {};
    const activeSubscription =
      subscribed && ends_at && new Date() <= new Date(ends_at);
    const isAlreadyPurchased =
      plan_type === 'plan'
        ? activeSubscription
        : purchases?.[getPurchaseKey(plan_type)]?.includes(
            Number(courseDetail?.id),
          );
    const isJoinDeepLinkFlow = Boolean(
      getItem(LOCAL_STORAGE_CONSTANT.JOIN_DEEP_LINK_FLOW),
    );
    const isClosedMobilityCoaching =
      Boolean(courseDetail?.attributes?.disabled_for_selling) &&
      normalizeSlug(courseDetail?.attributes?.slug) ===
        normalizeSlug(COURSES_SLUGS.MOBILITY_COACHING);

    if (isAlreadyPurchased) {
      const onBoardingCompleted = getUserAttribute('onboarding_completed');
      if (onBoardingCompleted || isJoinDeepLinkFlow) {
        navigate(route);
      } else {
        const url = getRoute(APP_ROUTES.FITNESS, {
          searchParam: {
            page: encodeBase64Value(route),
            special_purchase: true,
          },
        });
        navigate(url);
      }
    } else if (isClosedMobilityCoaching && isJoinDeepLinkFlow) {
      joinPlanWaitingListRequest(plan_id)
        .then(() => {
          navigate(`/${COURSES_SLUGS.MOBILITY_COACHING}`, {
            replace: true,
            state: { waitlistJoined: true },
          });
        })
        .catch(error => {
          const alreadyJoined = error?.response?.status === 422;
          if (!alreadyJoined) {
            createNotification({
              type: NOTIFICATION_TYPE.ERROR,
              message: extractApiError(error),
            });
          }
          navigate(`/${COURSES_SLUGS.MOBILITY_COACHING}`, {
            replace: true,
            state: { waitlistJoined: alreadyJoined },
          });
        });
    } else {
      const urlParams = new URLSearchParams(location.search);
      const page = urlParams.get('page');
      const slug =
        courseDetail?.attributes?.slug ||
        getSlugFromMatch(match) ||
        page ||
        getStoredCourseSlug();
      const payload = {
        product_id,
        plan_id,
        page: encodeBase64Value(route),
        name: encodeURI(title),
        errorCallBack: error => {
          if (isActivePurchaseError(error)) {
            navigate(route);
            return true;
          }
          setChallengeError(true);
          return false;
        },
        plan_type: slug ? encodeURI(slug) : undefined,
        isFromCoursePage: true, // indication that the order request came from course page
      };
      if (plan_type === 'plan' && selectedPlan && !isJoinDeepLinkFlow) {
        delete payload.product_id;
      }
      dispatch(doPaymentInitiationRequest(payload));
    }
  };

  // const handleCourseRedirection = () => {
  //   if (courseDetail) {
  //     const slug = params.slug;
  //     const selectedFilters = SLUGS_TO_FILTERS[slug];
  //     if (selectedFilters) {
  //       dispatch(
  //         setSelectedFilters({
  //           selectedFilters,
  //           callback: () => {
  //             redirectUser(courseDetail);
  //           },
  //         }),
  //       );
  //     } else {
  //       redirectUser(courseDetail);
  //     }
  //   } else {
  //     const urlParams = new URLSearchParams(location.search);
  //     const page = urlParams.get('page');
  //     let slug = params.slug || page;
  //     const p_interval = getItem('selectedPlan');
  //     const interval =
  //       JSON.parse(p_interval)?.attributes?.interval || p_interval;
  //       setIsCourseLoading(true);
  //     dispatch(
  //       doGetPublicPlansRequest({
  //         interval,
  //         callback: plans => {
  //           if (slug === 'Free Trial Page') {
  //             // Added due to issue caused when signup/login from /freetrial page
  //             slug = null;
  //           }
  //           const course = plans.find(plan => plan?.attributes?.slug === slug);
  //           if (course) {
  //             const selectedFilters = SLUGS_TO_FILTERS[slug];
  //             if (selectedFilters) {
  //               dispatch(
  //                 setSelectedFilters({
  //                   selectedFilters,
  //                   callback: () => {
  //                     redirectUser(course);
  //                   },
  //                 }),
  //               );
  //             } else {
  //               redirectUser(course);
  //             }
  //           } else {
  //             navigate(APP_ROUTES.CHALLENGES);
  //           }
  //         },
  //         // setLoading: setIsCourseLoading,
  //       }),
  //     );
  //   }
  // };

  const handleCourseRedirection = async () => {
    try {
      // setIsCourseLoading(true);
      dispatch(setUniversalLoading(true));
      const routeSlug = normalizeSlug(getCurrentSlug());
      let cachedCourseDetail = courseDetail;

      if (!cachedCourseDetail) {
        try {
          cachedCourseDetail = JSON.parse(
            sessionStorage.getItem('courseDetail'),
          );
        } catch (error) {
          cachedCourseDetail = null;
        }
      }

      const isCachedCourseForCurrentSlug =
        cachedCourseDetail &&
        (!routeSlug ||
          normalizeSlug(cachedCourseDetail?.attributes?.slug) === routeSlug);

      if (isCachedCourseForCurrentSlug) {
        const slug = getSlugFromMatch(match);
        const selectedFilters = SLUGS_TO_FILTERS[slug];

        const isJoinDeepLinkFlow = Boolean(
          getItem(LOCAL_STORAGE_CONSTANT.JOIN_DEEP_LINK_FLOW),
        );
        if (
          selectedFilters &&
          !isJoinDeepLinkFlow &&
          !isCourseAlreadyPurchased(cachedCourseDetail)
        ) {
          await new Promise(resolve => {
            dispatch(
              setSelectedFilters({
                selectedFilters,
                callback: resolve,
              }),
            );
          });
        }

        redirectUser(cachedCourseDetail);
        dispatch(setUniversalLoading(false));
        return;
      }

      // If courseDetail doesn't exist, fetch public plans
      const urlParams = new URLSearchParams(location.search);
      let slug =
        getSlugFromMatch(match) ||
        urlParams.get('page') ||
        getStoredCourseSlug();
      const p_interval = getItem('selectedPlan');
      const interval =
        JSON.parse(p_interval)?.attributes?.interval || p_interval;
      // let plans = [];
      // if(publicPlans.length === 0){
      const plans = await new Promise((resolve, reject) => {
        dispatch(
          doGetPublicPlansRequest({
            interval,
            callback: resolve,
            onError: reject, // Make sure doGetPublicPlansRequest supports this
          }),
        );
      });
      // }
      // else{
      //   plans = publicPlans;
      // }

      if (slug === 'Free Trial Page') {
        slug = null;
      }

      const course = plans?.find(
        plan => normalizeSlug(plan?.attributes?.slug) === normalizeSlug(slug),
      );
      if (course) {
        const selectedFilters = SLUGS_TO_FILTERS[slug];
        const isJoinDeepLinkFlow = Boolean(
          getItem(LOCAL_STORAGE_CONSTANT.JOIN_DEEP_LINK_FLOW),
        );
        if (
          selectedFilters &&
          !isJoinDeepLinkFlow &&
          !isCourseAlreadyPurchased(course)
        ) {
          await new Promise(resolve => {
            dispatch(
              setSelectedFilters({
                selectedFilters,
                callback: resolve,
              }),
            );
          });
        }
        redirectUser(course);
      } else {
        navigate(APP_ROUTES.CHALLENGES);
      }
      // setIsCourseLoading(false);
      dispatch(setUniversalLoading(false));
    } catch (error) {
      // setIsCourseLoading(false);
      dispatch(setUniversalLoading(false));
      console.error('Error during course redirection:', error);
      navigate(APP_ROUTES.CHALLENGES); // fallback in case of error
    }
  };

  return {
    challengeError,
    handleCourseRedirection,
    isLoading,
    setChallengeError,
    courseDetail,
    // isCourseLoading,
  };
};
